import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  Award, Wrench, MapPin, Layers, ShieldCheck, Zap, CreditCard, Headset,
  Sun, Eye, Sofa, Palette, Sparkles, Star, MessageCircle, Instagram,
} from "lucide-react";
import { SEO, LOCAL_BUSINESS_LD, WEBSITE_LD } from "@/components/site/SEO";
import { Reveal } from "@/components/site/Reveal";
import { Marquee } from "@/components/site/Marquee";
import { Simulador } from "@/components/site/Simulador";
import { InstagramHome } from "@/components/site/InstagramHome";
import { CountUp } from "@/components/site/CountUp";
import { waLink, WA_DEFAULT, waService } from "@/lib/wa";
import { trackWhatsApp, trackServiceSelect } from "@/lib/analytics";
import { useEffect, useState } from "react";
import { api, imgSrc } from "@/lib/api";
import { useSiteImages, slot, HOME_SERVICO_SLOTS, PORTFOLIO_SLOTS } from "@/lib/siteImages";

const DIFERENCIAIS = [
  { icon: Award, title: "Desde 2010", text: "Experiência em instalação profissional de insulfilm no Rio de Janeiro desde 2010." },
  { icon: Wrench, title: "Instalação profissional", text: "Equipe técnica especializada e acabamento impecável." },
  { icon: MapPin, title: "Todo o Rio de Janeiro", text: "Atendemos na sua residência, empresa, condomínio ou veículo." },
  { icon: Layers, title: "Materiais de qualidade", text: "Trabalhamos com películas de linhas reconhecidas." },
  { icon: ShieldCheck, title: "Garantia", text: "Garantia conforme o serviço e a película contratados, com acompanhamento após a instalação." },
  { icon: Zap, title: "Atendimento rápido", text: "Orçamento ágil pelo WhatsApp, sem burocracia." },
  { icon: CreditCard, title: "Pagamento facilitado", text: "10% de desconto no PIX e parcelamento em até 10x sem juros no cartão." },
  { icon: Headset, title: "Suporte completo", text: "Suporte antes e depois da instalação." },
];

const SERVICOS = [
  { img: "/images/residencial-janelas.webp", alt: "Insulfilm residencial aplicado em janelas", title: "Insulfilm Residencial",
    text: "Janelas, sacadas, varandas, coberturas e áreas gourmet com controle de luminosidade, rejeição solar e mais privacidade.", key: "insulfilm residencial" },
  { img: "/images/empresarial.webp", alt: "Película de controle solar em fachada comercial", title: "Insulfilm Empresarial e Comercial",
    text: "Escritórios, lojas, fachadas, condomínios e clínicas com visual profissional.", key: "insulfilm empresarial e comercial" },
  { img: "/images/automotivo-suv.webp", alt: "Insulfilm automotivo aplicado em SUV", title: "Insulfilm Automotivo",
    text: "Para-brisa, portas e vidro traseiro com películas de alta performance.", key: "insulfilm automotivo" },
  { img: "/images/seguranca-antiestilhaco.webp", alt: "Película de segurança aplicada em vidro", title: "Películas de Segurança",
    text: "Reforço para vidros, antivandalismo e proteção contra estilhaços.", key: "película de segurança" },
  { img: "/images/portfolio-jateado.webp", alt: "Película jateada em divisória de escritório", title: "Películas Decorativas",
    text: "Jateado, listradas, venezianas e padrões que renovam ambientes.", key: "película decorativa" },
  { img: "/images/mesa-tampo.webp", alt: "Aplicação de película em mesa de vidro", title: "Películas para Mesas",
    text: "Proteção e renovação para tampos de vidro com opções de cores e efeitos.", key: "película para mesa" },
];

const BENEFICIOS = [
  { icon: Sun, title: "Bloqueio de raios UV", text: "Ajuda a reduzir a ação dos raios UV sobre móveis, pisos, cortinas e objetos expostos ao sol." },
  { icon: Sparkles, title: "Rejeição solar", text: "Ajuda a controlar a entrada de energia solar e melhora o conforto do ambiente. O desempenho varia conforme a película escolhida." },
  { icon: Eye, title: "Controle da luminosidade", text: "Menos ofuscamento e mais conforto visual no dia a dia." },
  { icon: ShieldCheck, title: "Privacidade", text: "Películas com diferentes níveis de privacidade, conforme o modelo, a iluminação e o ambiente de aplicação." },
  { icon: Layers, title: "Proteção contra estilhaços", text: "Películas de segurança ajudam a reter fragmentos em caso de quebra." },
  { icon: Sofa, title: "Preservação de móveis", text: "Ajuda a reduzir o desbotamento de móveis, pisos e objetos expostos ao sol." },
  { icon: Zap, title: "Conforto visual", text: "Ambientes mais agradáveis para viver e trabalhar." },
  { icon: Palette, title: "Renovação estética", text: "Valoriza fachadas, divisórias e vidros com acabamento moderno." },
];

const ETAPAS = [
  { n: "01", title: "Você entra em contato", text: "Chame no WhatsApp ou envie o formulário de orçamento." },
  { n: "02", title: "Envia fotos e medidas", text: "Mande fotos do local e medidas aproximadas dos vidros." },
  { n: "03", title: "Recebe o orçamento", text: "Preparamos a proposta e orientamos na escolha da película." },
  { n: "04", title: "Agenda a instalação", text: "Instalamos no seu endereço, no melhor dia e horário para você." },
];

const PORTFOLIO = {
  Residencial: [
    { img: "/images/hero-residencial.webp", alt: "Insulfilm residencial aplicado em janelas de apartamento" },
    { img: "/images/residencial-janelas.webp", alt: "Película de controle solar em janelas residenciais" },
    { img: "/images/residencial-sacada.webp", alt: "Insulfilm residencial aplicado em janelas de sacada" },
  ],
  Empresarial: [
    { img: "/images/empresarial.webp", alt: "Película de controle solar em fachada comercial" },
    { img: "/images/portfolio-fachada.webp", alt: "Película de controle solar aplicada em fachada de prédio comercial" },
    { img: "/images/portfolio-espelhada.webp", alt: "Película espelhada aplicada em porta de vidro de empresa" },
  ],
  Automotivo: [
    { img: "/images/automotivo-suv.webp", alt: "Insulfilm automotivo aplicado em SUV" },
    { img: "/images/automotivo-fume.webp", alt: "Película fumê aplicada em vidros de veículo de passeio" },
  ],
  "Mesas e decoração": [
    { img: "/images/mesa-tampo.webp", alt: "Aplicação de película em mesa de vidro" },
    { img: "/images/portfolio-jateado.webp", alt: "Película jateada em porta de vidro de escritório" },
    { img: "/images/portfolio-fume-porta.webp", alt: "Película fumê aplicada em porta de vidro" },
  ],
};

function Hero() {
  const ref = useRef(null);
  const images = useSiteImages();
  const heroImg = slot(images, "hero", "/images/hero-residencial.webp", "Insulfilm residencial aplicado em janelas de apartamento no Rio de Janeiro");
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const lines = ["Insulfilm Residencial, ", "Comercial e Automotivo ", "no Rio de Janeiro"];

  return (
    <section ref={ref} className="grain relative flex min-h-[86vh] items-center overflow-hidden bg-navy pt-16 md:min-h-[94vh] md:pt-20" data-testid="hero-section">
      <motion.div style={{ y }} className="absolute inset-0">
        <img src={heroImg.src}
             srcSet={heroImg.src === "/images/hero-residencial.webp" ? "/images/hero-residencial-800.webp 800w, /images/hero-residencial-1200.webp 1200w, /images/hero-residencial.webp 1600w" : undefined}
             sizes={heroImg.src === "/images/hero-residencial.webp" ? "100vw" : undefined}
             alt={heroImg.alt} width="1600" height="900"
             className="h-full w-full scale-110 object-cover" fetchPriority="high" />
        <div className="absolute inset-0 bg-gradient-to-r from-navy via-navy/85 to-navy/40" />
      </motion.div>
      <div className="light-sweep pointer-events-none absolute inset-y-0 w-1/3 bg-gradient-to-r from-transparent via-white/25 to-transparent" aria-hidden="true" />
      <div className="relative mx-auto w-full max-w-7xl px-5 py-16 md:px-12 md:py-24">
        <motion.p
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
          className="font-display text-[11px] font-bold uppercase tracking-[0.3em] text-aqua md:text-xs"
        >
          Bella Film RJ · Desde 2010
        </motion.p>
        <h1 className="mt-4 font-display text-[27px] font-bold leading-[1.08] tracking-tight text-white sm:text-5xl md:mt-6 lg:text-6xl" data-testid="hero-title">
          {lines.map((line, i) => (
            <span key={i} className="block overflow-hidden pb-1">
              <motion.span
                className="block"
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ duration: 0.9, delay: 0.15 + i * 0.13, ease: [0.22, 1, 0.36, 1] }}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>
        <motion.p
          initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.65 }}
          className="mt-4 max-w-xl text-[13px] leading-relaxed text-white/80 md:mt-6 md:text-lg"
        >
          Instalação profissional de insulfilm para residências, empresas e veículos no Rio de Janeiro.
          Películas para rejeição solar, controle de luminosidade, bloqueio UV, privacidade, segurança e renovação de ambientes.
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.85 }}
          className="mt-7 flex flex-col gap-3 sm:flex-row sm:flex-wrap md:mt-10 md:gap-4"
        >
          <a href={waLink(WA_DEFAULT)} target="_blank" rel="noopener noreferrer" data-testid="hero-cta-whatsapp"
             onClick={() => trackWhatsApp("hero", "Solicitar orçamento")}
             className="inline-flex items-center justify-center gap-2 rounded-full bg-aqua px-7 py-4 font-display text-sm font-bold text-white transition-all hover:-translate-y-1 hover:bg-aqua-dark hover:shadow-xl md:px-8 md:py-4 md:text-base">
            <MessageCircle size={19} /> Pedir orçamento no WhatsApp
          </a>
          <a href="https://www.instagram.com/bellafilmrj/" target="_blank" rel="noopener noreferrer" data-testid="hero-cta-instagram"
             className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-white/50 px-7 py-3.5 font-display text-sm font-semibold text-white transition-colors hover:border-aqua hover:text-aqua sm:hidden">
            <Instagram size={17} /> Instagram @bellafilmrj
          </a>
          <Link to="/servicos" data-testid="hero-cta-servicos"
                className="hidden items-center justify-center rounded-full border-2 border-white/60 px-7 py-3.5 font-display text-sm font-semibold text-white transition-all hover:border-white hover:bg-white hover:text-navy sm:inline-flex md:px-8 md:py-4 md:text-base">
            Conhecer os serviços
          </Link>
          <Link to="/catalogo-insulfilm-rj" data-testid="hero-cta-catalogo"
                className="hidden items-center justify-center rounded-full border-2 border-transparent px-8 py-4 font-display text-base font-semibold text-white/85 underline-offset-4 transition-colors hover:text-aqua hover:underline sm:inline-flex">
            Ver catálogo de películas
          </Link>
        </motion.div>
      </div>
    </section>
  );
}

function Reviews() {
  const [reviews, setReviews] = useState([]);
  const [settings, setSettings] = useState({});
  useEffect(() => {
    api.get("/reviews").then((r) => setReviews(r.data)).catch(() => {});
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);
  if (!reviews.length) return null;
  return (
    <section className="bg-white py-14 md:py-32" data-testid="reviews-section">
      <div className="mx-auto max-w-7xl px-5 md:px-12">
        <Reveal>
          <p className="font-display text-xs font-bold uppercase tracking-[0.3em] text-aqua">Avaliações</p>
          <h2 className="mt-3 font-display text-2xl font-bold tracking-tight text-navy md:mt-4 md:text-4xl">
            O que dizem nossos clientes no Google
          </h2>
        </Reveal>
        <div className="mt-8 grid gap-4 md:mt-12 md:grid-cols-3 md:gap-6">
          {reviews.map((r, i) => (
            <Reveal key={r.id} delay={i * 0.08}>
              <figure className="h-full rounded-xl border border-navy/10 bg-mist p-6 md:p-8" data-testid={`review-card-${i}`}>
                <div className="flex gap-1 text-aqua" role="img" aria-label={`Nota ${r.rating} de 5`}>
                  {Array.from({ length: r.rating }).map((_, s) => <Star key={s} size={16} fill="currentColor" />)}
                </div>
                <blockquote className="mt-4 text-sm leading-relaxed text-navy/80">{r.text}</blockquote>
                <figcaption className="mt-5 font-display text-sm font-bold text-navy">
                  {r.name}
                  {r.date && <span className="ml-2 font-sans font-normal text-navy/50">{r.date}</span>}
                </figcaption>
              </figure>
            </Reveal>
          ))}
        </div>
        {settings.google_reviews_url && (
          <div className="mt-10">
            <a href={settings.google_reviews_url} target="_blank" rel="noopener noreferrer" data-testid="reviews-google-link"
               className="font-display text-sm font-bold text-bella underline underline-offset-4 hover:text-aqua">
              Ver avaliações no Google
            </a>
          </div>
        )}
      </div>
    </section>
  );
}

export default function Home() {
  const [tab, setTab] = useState("Residencial");
  const [settings, setSettings] = useState({});
  const images = useSiteImages();
  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);
  return (
    <>
      <SEO
        title="Insulfilm RJ | Residencial, Comercial e Automotivo | Bella Film"
        description="Insulfilm residencial, comercial e automotivo no Rio de Janeiro. Instalação profissional, proteção solar, bloqueio UV e orçamento pelo WhatsApp."
        path="/"
        jsonLd={[LOCAL_BUSINESS_LD, WEBSITE_LD]}
      />
      <Hero />
      <Marquee />

      <div className="flex flex-col">
      <section className="order-6 bg-white py-14 md:order-none md:py-32" data-testid="diferenciais-section">
        <div className="mx-auto max-w-7xl px-5 md:px-12">
          <Reveal>
            <p className="font-display text-xs font-bold uppercase tracking-[0.3em] text-aqua">Diferenciais</p>
            <h2 className="mt-3 max-w-2xl font-display text-xl font-bold tracking-tight text-navy md:mt-4 md:text-5xl">
              Por que escolher a Bella Film RJ
            </h2>
          </Reveal>
          <div className="mt-8 grid grid-cols-2 gap-3 md:mt-14 md:gap-5 lg:grid-cols-4">
            {DIFERENCIAIS.map((d, i) => (
              <Reveal key={d.title} delay={(i % 4) * 0.08}>
                <div className="group h-full rounded-xl border border-navy/10 bg-white p-5 transition-all hover:-translate-y-1 hover:border-aqua/40 hover:shadow-xl md:p-7" data-testid={`diferencial-card-${i}`}>
                  <d.icon size={22} className="text-aqua" aria-hidden="true" />
                  <h3 className="mt-3 font-display text-sm font-bold leading-snug text-navy md:mt-4 md:text-lg">{d.title}</h3>
                  <p className="mt-2 hidden text-sm leading-relaxed text-navy/65 sm:block">{d.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="grain relative order-1 bg-navy py-14 text-white md:order-none md:py-32" data-testid="servicos-section">
        <div className="mx-auto max-w-7xl px-5 md:px-12">
          <Reveal>
            <p className="font-display text-xs font-bold uppercase tracking-[0.3em] text-aqua">Principais serviços</p>
            <h2 className="mt-3 max-w-2xl font-display text-xl font-bold tracking-tight md:mt-4 md:text-5xl">
              Soluções em películas para cada projeto
            </h2>
          </Reveal>
          <div className="mt-8 grid gap-4 sm:grid-cols-2 md:mt-14 md:gap-6 lg:grid-cols-3">
            {SERVICOS.map((s, i) => {
              const cardImg = slot(images, HOME_SERVICO_SLOTS[i], s.img, s.alt);
              return (
              <Reveal key={s.title} delay={(i % 3) * 0.1}>
                <article className="group flex h-full flex-col overflow-hidden rounded-xl bg-navy-soft ring-1 ring-white/10 transition-all hover:-translate-y-1 hover:ring-aqua/40" data-testid={`servico-card-${i}`}>
                  <div className="overflow-hidden">
                    <img src={cardImg.src} srcSet={cardImg.srcSet} sizes={cardImg.sizes} width={cardImg.width} height={cardImg.height}
                         alt={cardImg.alt} loading="lazy" data-testid={`servico-card-img-${i}`}
                         className="aspect-[16/9] w-full object-cover transition-transform duration-700 group-hover:scale-105 md:aspect-[16/10]" />
                  </div>
                  <div className="flex flex-1 flex-col p-5 md:p-7">
                    <h3 className="font-display text-lg font-bold md:text-xl">{s.title}</h3>
                    <p className="mt-2 hidden flex-1 text-sm leading-relaxed text-white/70 sm:block">{s.text}</p>
                    <a href={waLink(waService(s.key))} target="_blank" rel="noopener noreferrer"
                       data-testid={`servico-card-cta-${i}`}
                       aria-label={`Solicitar orçamento de ${s.title}`}
                       onClick={() => { trackWhatsApp("home_servicos", s.title); trackServiceSelect(s.title); }}
                       className="mt-4 inline-flex w-fit items-center gap-2 rounded-full bg-aqua px-5 py-2.5 font-display text-xs font-bold text-white transition-colors hover:bg-aqua-dark md:mt-5 md:text-sm">
                      <MessageCircle size={15} /> Solicitar orçamento
                    </a>
                  </div>
                </article>
              </Reveal>
              );
            })}
          </div>
          <Reveal className="mt-8 md:mt-10">
            <Link to="/servicos" data-testid="servicos-ver-todos"
                  className="font-display text-sm font-bold text-aqua underline underline-offset-4 hover:text-white">
              Ver todos os serviços em detalhe
            </Link>
          </Reveal>
        </div>
      </section>

      <section className="order-3 bg-mist py-14 md:order-none md:py-32" data-testid="beneficios-section">
        <div className="mx-auto max-w-7xl px-5 md:px-12">
          <Reveal>
            <p className="font-display text-xs font-bold uppercase tracking-[0.3em] text-aqua">Benefícios</p>
            <h2 className="mt-3 max-w-2xl font-display text-xl font-bold tracking-tight text-navy md:mt-4 md:text-5xl">
              O que as películas podem fazer por você
            </h2>
          </Reveal>
          <div className="mt-8 grid grid-cols-2 gap-3 md:mt-14 md:gap-5 lg:grid-cols-4">
            {BENEFICIOS.map((b, i) => (
              <Reveal key={b.title} delay={(i % 4) * 0.08}>
                <div className="h-full rounded-xl bg-white p-5 shadow-sm transition-all hover:-translate-y-1 hover:shadow-lg md:p-7" data-testid={`beneficio-card-${i}`}>
                  <b.icon size={20} className="text-bella" aria-hidden="true" />
                  <h3 className="mt-3 font-display text-sm font-bold leading-snug text-navy md:mt-4 md:text-base">{b.title}</h3>
                  <p className="mt-2 hidden text-sm leading-relaxed text-navy/65 sm:block">{b.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal>
            <p className="mt-6 text-xs text-navy/55 md:mt-8 md:text-sm">
              Os benefícios variam conforme o modelo da película escolhida. A Bella Film orienta você na seleção ideal para cada ambiente.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="grain relative order-4 bg-navy py-14 text-white md:order-none md:py-24" data-testid="prova-social-section">
        <div className="mx-auto max-w-7xl px-5 md:px-12">
          <div className="grid gap-10 text-center sm:grid-cols-3">
            <Reveal>
              <p className="font-display text-4xl font-bold text-aqua md:text-6xl" data-testid="stat-clientes">
                <CountUp end={10000} prefix="+" />
              </p>
              <p className="mt-2 font-display text-xs font-semibold uppercase tracking-[0.18em] text-white/70 md:text-sm">clientes atendidos</p>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="font-display text-4xl font-bold text-aqua md:text-6xl" data-testid="stat-desde">2010</p>
              <p className="mt-2 font-display text-xs font-semibold uppercase tracking-[0.18em] text-white/70 md:text-sm">Desde 2010 no Rio de Janeiro</p>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="font-display text-4xl font-bold text-aqua md:text-6xl" data-testid="stat-instagram">
                {settings.instagram_followers || "@bellafilmrj"}
              </p>
              <p className="mt-2 font-display text-xs font-semibold uppercase tracking-[0.18em] text-white/70 md:text-sm">
                {settings.instagram_followers ? "seguidores no Instagram" : "siga a Bella Film no Instagram"}
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="order-7 bg-white py-14 md:order-none md:py-32" data-testid="como-funciona-section">
        <div className="mx-auto max-w-7xl px-5 md:px-12">
          <Reveal>
            <p className="font-display text-xs font-bold uppercase tracking-[0.3em] text-aqua">Como funciona</p>
            <h2 className="mt-3 font-display text-xl font-bold tracking-tight text-navy md:mt-4 md:text-5xl">
              Orçamento em 4 etapas
            </h2>
          </Reveal>
          <div className="mt-9 grid grid-cols-2 gap-6 md:mt-16 md:grid-cols-4 md:gap-10">
            {ETAPAS.map((e, i) => (
              <Reveal key={e.n} delay={i * 0.1}>
                <div data-testid={`etapa-${i + 1}`}>
                  <span className="font-display text-5xl font-bold leading-none text-aqua md:text-8xl" aria-hidden="true">{e.n}</span>
                  <h3 className="mt-2 font-display text-base font-bold leading-snug text-navy md:mt-3 md:text-xl">{e.title}</h3>
                  <p className="mt-2 text-xs leading-relaxed text-navy/65 md:text-sm">{e.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="order-8 bg-mist py-14 md:order-none md:py-32" data-testid="portfolio-section">
        <div className="mx-auto max-w-7xl px-5 md:px-12">
          <Reveal>
            <p className="font-display text-xs font-bold uppercase tracking-[0.3em] text-aqua">Portfólio</p>
            <h2 className="mt-3 font-display text-xl font-bold tracking-tight text-navy md:mt-4 md:text-5xl">
              Trabalhos reais da Bella Film
            </h2>
          </Reveal>
          <div className="mt-7 flex flex-wrap gap-2 md:mt-10 md:gap-3" role="tablist" aria-label="Categorias do portfólio">
            {Object.keys(PORTFOLIO).map((k) => (
              <button key={k} role="tab" aria-selected={tab === k} data-testid={`portfolio-tab-${k.toLowerCase().replace(/\s/g, "-")}`}
                      onClick={() => setTab(k)}
                      className={`rounded-full px-4 py-2 font-display text-xs font-semibold transition-colors md:px-6 md:py-2.5 md:text-sm ${
                        tab === k ? "bg-navy text-white" : "bg-white text-navy ring-1 ring-navy/15 hover:ring-aqua"}`}>
                {k}
              </button>
            ))}
          </div>
          <div className="mt-7 grid gap-4 sm:grid-cols-2 md:mt-10 md:gap-6 lg:grid-cols-3">
            {PORTFOLIO[tab].map((p, i) => (
              <Reveal key={p.img} delay={i * 0.08}>
                <div className="group overflow-hidden rounded-xl" data-testid={`portfolio-item-${i}`}>
                  <img src={slot(images, PORTFOLIO_SLOTS[tab][i], p.img, p.alt).src} alt={slot(images, PORTFOLIO_SLOTS[tab][i], p.img, p.alt).alt} loading="lazy"
                       className="aspect-[4/3] w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <Simulador />

      <InstagramHome />

      <div className="order-9 md:order-none">
        <Reviews />
      </div>

      <section className="grain relative order-10 bg-navy py-14 text-white md:order-none md:py-24" data-testid="pix-banner">
        <div className="mx-auto flex max-w-7xl flex-col items-start gap-6 px-5 md:flex-row md:items-center md:justify-between md:px-12">
          <Reveal>
            <p className="font-display text-xs font-bold uppercase tracking-[0.3em] text-white/60">Condição especial</p>
            <h2 className="mt-3 font-display text-2xl font-bold tracking-tight text-aqua md:text-5xl">10% de desconto no PIX</h2>
            <p className="mt-3 max-w-lg text-sm leading-relaxed text-white/75 md:text-base">
              Parcele em até 10 vezes sem juros no cartão. Recebeu uma proposta da concorrência? Envie para nossa análise e consulte as condições disponíveis.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <a href={waLink("Olá! Encontrei a Bella Film RJ pelo site e quero aproveitar o desconto de 10% no PIX. Pode me passar um orçamento?")}
               target="_blank" rel="noopener noreferrer" data-testid="pix-cta-whatsapp"
               onClick={() => trackWhatsApp("pix_banner", "10% PIX")}
               className="inline-flex items-center gap-2 rounded-full bg-aqua px-7 py-3.5 font-display text-sm font-bold text-white transition-all hover:-translate-y-1 hover:bg-aqua-dark hover:shadow-xl md:px-8 md:py-4 md:text-base">
              <MessageCircle size={19} /> Quero o desconto
            </a>
          </Reveal>
        </div>
      </section>

      <section className="order-11 bg-white py-14 md:order-none md:py-32" data-testid="cta-final-section">
        <div className="mx-auto max-w-7xl px-5 md:px-12">
          <Reveal>
            <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-bella to-navy p-7 text-white md:p-16">
              <div className="grain absolute inset-0" aria-hidden="true" />
              <h2 className="relative max-w-2xl font-display text-xl font-bold tracking-tight md:text-5xl">
                Precisa instalar insulfilm no Rio de Janeiro?
              </h2>
              <p className="relative mt-4 max-w-xl text-sm leading-relaxed text-white/80 md:text-base">
                Envie fotos, medidas aproximadas e o bairro da instalação. A Bella Film prepara seu orçamento e orienta você na escolha da película.
              </p>
              <a href={waLink(WA_DEFAULT)} target="_blank" rel="noopener noreferrer" data-testid="cta-final-whatsapp"
                 onClick={() => trackWhatsApp("cta_final", "Chamada final")}
                 className="relative mt-6 inline-flex items-center gap-2 rounded-full bg-white px-7 py-3.5 font-display text-sm font-bold text-navy transition-all hover:-translate-y-1 hover:shadow-xl md:mt-8 md:px-8 md:py-4 md:text-base">
                <MessageCircle size={19} /> Solicitar orçamento pelo WhatsApp
              </a>
            </div>
          </Reveal>
        </div>
      </section>
      </div>
    </>
  );
}
